import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useRouter } from 'next/router';
import {
  isEmpty, debounce,
} from 'lodash';
import { AssignmentInd } from '@mui/icons-material';
import { Avatar, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useApi } from 'lib/contexts/ApplicationState';
import { FilterResult } from 'lib/types/filters';
import {
  clearQuery, toSlug, updateRouterQuery,
} from 'lib/utils/filterUtils';
import { filterOptions, formatFilterResults } from 'components/MultiFilters/utils';
import { DEBOUNCE_TIME } from 'lib/constants';

const defaultLabel = 'Search for collaborators';
interface Props {
  filters: FilterResult[];
  label?: string;
}

export const CollaboratorMultiFilter = ({
  filters,
  label,
}: Props) => {
  const API = useApi();
  const router = useRouter();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<FilterResult[]>([]);

  const isFetching = useRef(false);

  const filterCreatorOptions = useMemo(
    () => debounce(async (input, callback) => {
      try {
        const searchResults = await API.getCollaboratorRolesFilterSearch(input);
        const formattedResults = formatFilterResults(searchResults, true, true);
        callback(formattedResults);
        return null;
      } catch (error) {
        isFetching.current = false;
        return null;
      }
    }, DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    isFetching.current = true;
    let active = true;
    if (isEmpty(inputValue)) {
      setOptions([]);
      return undefined;
    }

    filterCreatorOptions(inputValue, (results: FilterResult[]) => {
      isFetching.current = false;
      if (active && results) {
        setOptions(results);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue]);

  const renderOptionImg = (option: FilterResult) => {
    switch (option.query) {
    case 'byCollaborator':
      return (<Avatar alt={option.label} src={option.imgUrl} />);
    default:
      return (<div className="mr-6"><AssignmentInd fontSize="large" color="secondary" /></div>);
    }
  };

  const renderSelectedLabel = (option: FilterResult) => {
    switch (option.query) {
    case 'byCollaborator':
      return `Worked with @${option.label}`;

    default:
      return `Worked with ${option.label}`;
    }
  };

  const onChange = (values: FilterResult[]) => {
    const buildMultiQuery = () => values.reduce(
      (acc, value) => {
        const key: string = value.query;
        const slug = toSlug(value.label, value.id);
        if (!acc[key]) {
          acc[key] = [slug];
        } else {
          acc[key].push(slug);
        }

        return acc;
      }, {} as any,
    );

    const query = {
      ...clearQuery(['byCollaboratorType', 'byCollaboratorRole', 'byCollaborator']),
      ...buildMultiQuery(),
    };
    updateRouterQuery(router, query);
  };

  return (
    <Autocomplete
      multiple
      loading={!!inputValue?.length && isFetching.current}
      value={filters}
      filterOptions={filterOptions}
      className="mx-auto"
      autoHighlight
      noOptionsText="ex: a brand, company, creator, sports team, industry, etc."
      getOptionLabel={(option) => renderSelectedLabel(option)}
      options={[...options]}
      onChange={(_, v: FilterResult[]) => onChange(v)}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue.trim());
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Collaborators"
          placeholder={label || defaultLabel}
          fullWidth
          variant="outlined"
        />
      )}
      //@ts-ignore
      renderOption={
        (props, option: FilterResult) => (
          <li {...props} key={option.id}>
            <div className="flex w-full">
              <div className="w-20">
                {renderOptionImg(option)}
              </div>
              <div className="w-4/5">
                <div className="font-bold">
                  {option.label}
                </div>
                <div className="text-mediumgray text-sm">
                  {option.subLabel}
                </div>
              </div>
            </div>
          </li>
        )
      }
      size="small"
    />
  );
};
